import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// import { usePagination, DOTS } from '@/hooks/usePagination'; // Assuming hook exists
import { PageInfo } from '@/interfaces/common.interface';

interface PageOptions {
  label: string;
  value: number;
}

interface PaginationProps {
  pageOptions?: PageOptions[];
  paginationData: PageInfo;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  isLoading?: boolean;
}

const PageDropdownSelect: React.FC<{
  options: PageOptions[];
  selected: number;
  onSelect: (value: number) => void;
}> = ({ options, selected, onSelect }) => (
  <select
    value={selected}
    onChange={(e) => onSelect(Number(e.target.value))}
    className="border border-gray-300 rounded-md p-1 text-sm"
  >
    {options.map((opt) => (
      <option key={opt.value} value={opt.value}>
        {opt.label}
      </option>
    ))}
  </select>
);

const options = [
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

const Pagination: React.FC<PaginationProps> = ({
  pageOptions = options,
  paginationData,
  onPageChange,
  onPageSizeChange = () => {},
  isLoading = false,
}) => {
  const { page, totalPages, limit, total } = paginationData;

  const pageNumbers = useMemo(() => {
    const pagesToShow = 5;
    let startPage = Math.max(1, page - Math.floor(pagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + pagesToShow - 1);

    if (endPage - startPage + 1 < pagesToShow) {
      startPage = Math.max(1, endPage - pagesToShow + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }, [page, totalPages]);

  // if (page === 0 || totalPages < 2) {
  //   return null;
  // }

  const firstItem = (page - 1) * limit + 1;
  const lastItem = Math.min(page * limit, total);

  return (
    <div className="flex flex-col md:flex-row items-center justify-between w-full p-4 gap-4">
      <div className="flex items-center gap-4">
        {paginationData?.totalPages > 1 && (
          <>
            <PageDropdownSelect
              // options={[10, 20, 30, 40, 50, 75, 100]}
              options={pageOptions}
              selected={limit}
              onSelect={onPageSizeChange}
            />
          </>
        )}
        <p className="text-sm text-gray-500">
          Showing{' '}
          <span className="font-semibold text-gray-800">{firstItem}</span> to{' '}
          <span className="font-semibold text-gray-800">{lastItem}</span> of{' '}
          <span className="font-semibold text-gray-800">{total}</span> entries
        </p>
      </div>

      {paginationData?.totalPages > 1 && (
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => page > 1 && onPageChange(page - 1)}
            disabled={page === 1 || isLoading}
            className="p-2 text-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed"
          >
            <ChevronLeft />
          </button>

          {pageNumbers.map((pageNum) => {
            // if (pageNum === DOTS) {
            //   return (
            //     <span
            //       key={`dots-${index}`}
            //       className="w-10 h-10 flex items-center justify-center"
            //     >
            //       &#8230;
            //     </span>
            //   );
            // }
            return (
              <button
                key={pageNum}
                type="button"
                onClick={() => onPageChange(pageNum as number)}
                disabled={isLoading}
                className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium
                ${
                  page === pageNum
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-200'
                }
                ${isLoading ? 'cursor-not-allowed' : ''}
              `}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => page < totalPages && onPageChange(page + 1)}
            disabled={page === totalPages || isLoading}
            className="p-2 text-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed"
          >
            <ChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
